/*============================================================================
  HC - IB cart drawer v2 nudges + savings ledger - 2026-08-20

  Fills the three slots snippets/ib-cart-drawer.liquid reserves:
    [data-ib-nudges]      the pinned strip between the header and the form
    [data-ib-nudges-met]  green "already earned" rows under the item list
    [data-ib-ledger]      the savings row above the subtotal

  Loaded from layout/theme.liquid inside the same surface gate as
  ib-cart-drawer.js and AFTER it, so window.IBCartDrawer already exists and the
  offers modal can be opened through the controller's own modal helpers (which
  is also what makes Escape and scrim clicks work — this file registers no
  global key handler of its own).

  It fills the same three slots on the v2 cart page (snippets/ib-cart-page.liquid),
  which reproduces the drawer's footer markup — [data-subtotal] inside
  .ib-cart-foot__totals — precisely so the gross strikethrough below has
  somewhere to land. The only surface difference is where first-paint cart state
  comes from: the drawer warms on open, the page is seeded inline. See
  readSeededCart().

  THREE THINGS THIS FILE MUST NEVER DO:

  1. Fetch on the add path. The manifest arrives via window.IBGiftManifest,
     which the gift-engine snippet already resolved once for the page, and cart
     state arrives on the event bus in payloads the add response already
     carried. The single getCart() below fires only when the drawer is opened
     cold with no state ever seen — never while an add is in flight.
  2. Duplicate the gift engine's threshold math. Everything promotional comes
     from IBGiftEngine.progress(), which is a projection of the same private
     helpers evaluate() uses (see tests/gift-engine/progress-parity.test.js).
  3. Take the drawer down with it. Every entry point is wrapped: engine absent
     or manifest null degrades to shipping-only, and an actual throw removes
     the strip from the document rather than leaving a half-painted row.

  A nudge is a PROMISE. Only rules present in snippets/ib-cart-nudge-copy.liquid
  render, so an unreviewed or retired promotion is silent rather than wrong.
==============================================================================*/
(function() {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-cart-drawer]',
    pageRoot: '[data-ib-cart-page]',
    seededCart: '[data-ib-cart-state]',
    pinned: '[data-ib-nudges]',
    met: '[data-ib-nudges-met]',
    ledger: '[data-ib-ledger]',
    copy: '[data-ib-nudge-copy]',
    totals: '.ib-cart-foot__totals',
    subtotal: '[data-subtotal]',
    items: '.cart__items',
    row: '.cart__item',
    qtyInput: '.js-qty__num',
    offersModal: '[data-ib-cart-modal="offers"]',
    offersList: '[data-ib-offers-list]',
    offersEmpty: '[data-ib-offers-empty]',
    moreBtn: '[data-ib-nudges-more]'
  };

  var REPAINT_MS = 120;
  var GROSS_CLASS = 'ib-cart-foot__gross';

  var CHECK_SVG = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"' +
    ' stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M2 8l4 4 8-8"/></svg>';

  /*==========================================================================
    Utilities
  ==========================================================================*/

  function escapeHtml(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(cents) {
    var value = parseInt(cents, 10);
    if (isNaN(value)) {
      return '';
    }
    // Deliberately NOT theme.Currency.formatMoney: this store's money format
    // carries HTML ($65<sup>80</sup>), which is unsafe for textContent writes
    // and wrong for the plain-money style these surfaces use (per the mockup).
    var dollars = Math.floor(Math.abs(value) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var centsPart = String(Math.abs(value) % 100);
    if (centsPart.length < 2) {
      centsPart = '0' + centsPart;
    }
    return (value < 0 ? '-' : '') + '$' + dollars + '.' + centsPart;
  }

  // Mirrors theme.liquid's window.theme.strings bridge, which substitutes
  // [token] placeholders at runtime. Falls back to English so the strip still
  // reads correctly if the bridge ever drops a key.
  //
  // HC - token substitution (PR E) - 2026-08-20: callers that pass a `tokens`
  // map get the placeholders filled in here rather than at each call site.
  // Two-argument callers are unchanged.
  // Shopify's `t` filter HTML-escapes every non-_html translation, so bridged
  // strings arrive entity-encoded ("You&#39;ve ..."). Decode ONCE here; the
  // renderers escape at insertion time, so without this the entities were
  // escaped twice and showed literally. A detached textarea decodes without
  // ever executing markup.
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) {
      return value;
    }
    return value
      .replace(/&#0*39;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function themeString(key, fallback, tokens) {
    var strings = typeof theme !== 'undefined' && theme.strings ? theme.strings : null;
    var value = strings && typeof strings[key] === 'string' ? decodeEntities(strings[key]) : '';
    value = value || fallback;
    if (!tokens) {
      return value;
    }
    for (var token in tokens) {
      if (Object.prototype.hasOwnProperty.call(tokens, token)) {
        value = value.split('[' + token + ']').join(tokens[token]);
      }
    }
    return value;
  }

  function setHidden(el, hidden) {
    if (!el) {
      return;
    }
    if (hidden) {
      el.hidden = true;
      el.setAttribute('hidden', 'hidden');
    } else {
      el.hidden = false;
      el.removeAttribute('hidden');
    }
  }

  function intFrom(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  /*==========================================================================
    Model — pure, and deliberately separable from the DOM.

    Everything the strip decides happens here: what gets pinned, what collapses
    into a green row, and how many offers hide behind the "+N" button. Kept
    free of element references so tests can drive it directly.
  ==========================================================================*/

  /*
   * Pinned-slot priority, in the plan's order:
   *   1. free shipping, while it is unmet — it is the one nudge that applies
   *      to every line in the cart, so it outranks any single promotion;
   *   2. otherwise the CLOSEST partially-progressed promotion (highest pct,
   *      ties broken by the smaller remaining amount, then by ruleId so the
   *      order never flickers between repaints);
   *   3. otherwise the free-shipping confirmation, if there is a threshold at
   *      all. Guests and untagged customers get threshold 0 and no row.
   * Nothing to say in any of the three -> the strip stays hidden entirely.
   */
  function buildModel(input) {
    var thresholdCents = intFrom(input.thresholdCents);
    var totalCents = intFrom(input.totalCents);
    var copy = input.copy || {};
    var rows = input.rows || [];
    var hasItems = input.itemCount > 0;

    /*
     * Shipping runs off cart.total_price AS-IS, reward lines included. That is
     * deliberate parity with the v1 free-shipping indicator
     * (theme.CartForm.updateFreeShippingIndicator feeds it the same number),
     * and it is also the honest one: Shopify charges shipping on the whole
     * cart, so a $0 gift line moves the total by $0 anyway. Promotional
     * progress is the opposite case and DOES exclude rewards — that exclusion
     * lives in the gift engine's qualifyingAmount, not here.
     */
    var shipping = {
      active: thresholdCents > 0 && hasItems,
      threshold: thresholdCents,
      total: totalCents,
      met: totalCents >= thresholdCents,
      remaining: Math.max(0, thresholdCents - totalCents),
      pct: thresholdCents > 0 ? Math.max(0, Math.min(1, totalCents / thresholdCents)) : 0
    };

    var candidates = [];
    var earned = [];

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var entry = copy[row.ruleId];
      if (!entry) {
        continue;
      }
      var decorated = {
        ruleId: row.ruleId,
        pct: row.pct,
        remaining: row.remaining,
        metric: row.metric,
        unitsEarned: row.unitsEarned,
        mode: row.mode || null,
        tierGroup: row.tierGroup || null,
        min: row.min || 0,
        copy: entry
      };
      // A recurring rule can be BOTH: one gift already earned (green row) and
      // partway to the next (progress row). That is the honest reading of a
      // "buy 12, get 1" rule sitting at 20 units, so both are emitted.
      //
      // HC - deals are the exception (Noah, 2026-09-02): an intro deal is a
      // one-time offer in spirit. The moment a salon hits 48 CODE+ shades the
      // strip must NOT say "add 48 more to unlock the Large deal" — the engine
      // still honours a second bundle at 96 if a cart gets there on its own,
      // but nothing pushes for it. So a deal row is a progress candidate only
      // while ITS OWN rule has nothing earned. Progress toward a HIGHER,
      // unearned tier (Medium earned at 30, "add 18 more for Large") is a real
      // step-up and stays; the tier filter below handles the superseded side.
      var repeatOfEarnedDeal = decorated.mode === 'deal' && row.unitsEarned >= 1;
      if (row.pct > 0 && row.pct < 1 && !repeatOfEarnedDeal) {
        candidates.push(decorated);
      }
      if (row.unitsEarned >= 1) {
        earned.push(decorated);
      }
    }

    /* HC - deal tiers (2026-09-01): rules that share a tierGroup are tiers of
       one offer (24 -> Medium, 48 -> Large). Once a higher tier is earned the
       lower one is superseded — the engine no longer offers it — so its
       "unlocked" row would point at a card that is not there. Keep only the
       highest earned tier per group. Progress toward the NEXT tier still shows. */
    var topEarnedByTier = {};
    for (var te = 0; te < earned.length; te++) {
      var tg = earned[te].tierGroup;
      if (!tg) continue;
      if (!topEarnedByTier[tg] || earned[te].min > topEarnedByTier[tg].min) topEarnedByTier[tg] = earned[te];
    }
    earned = earned.filter(function(e) {
      return !e.tierGroup || topEarnedByTier[e.tierGroup] === e;
    });
    /* HC - deal tiers, progress half - 2026-09-01: the same supersession
       applies to PROGRESS rows, which is where it actually bites. At 60 CODE+
       shades Medium (24-min) sits at 60/72 and Large (48-min) at 60/96, so the
       closest-first sort pins "add 12 more for another Medium" — an offer the
       engine stopped making the moment Large was earned. Drop any progress row
       whose group already has a HIGHER earned tier; rows with no tierGroup, and
       the top tier's own progress toward its next multiple, are untouched. */
    candidates = candidates.filter(function(c) {
      var top = c.tierGroup ? topEarnedByTier[c.tierGroup] : null;
      return !top || top.min <= c.min;
    });

    candidates.sort(function(a, b) {
      if (b.pct !== a.pct) return b.pct - a.pct;
      if (a.remaining !== b.remaining) return a.remaining - b.remaining;
      return a.ruleId < b.ruleId ? -1 : (a.ruleId > b.ruleId ? 1 : 0);
    });

    var pinned = null;
    var moreCount = 0;

    if (shipping.active && !shipping.met) {
      pinned = { kind: 'shipping-unmet', shipping: shipping };
      moreCount = candidates.length;
    } else if (candidates.length) {
      pinned = { kind: 'promo', promo: candidates[0] };
      moreCount = candidates.length - 1;
    } else if (shipping.active && shipping.met) {
      pinned = { kind: 'shipping-met', shipping: shipping };
    }

    var metRows = [];
    if (shipping.active && shipping.met && (!pinned || pinned.kind !== 'shipping-met')) {
      metRows.push({ kind: 'shipping', message: themeString('cartFreeShippingReached', "You've unlocked free shipping") });
    }
    for (var e = 0; e < earned.length; e++) {
      metRows.push({
        kind: 'promo',
        ruleId: earned[e].ruleId,
        message: earned[e].copy.met || earned[e].copy.label || ''
      });
    }

    return {
      shipping: shipping,
      pinned: pinned,
      candidates: candidates,
      metRows: metRows,
      moreCount: moreCount
    };
  }

  /*
   * Savings ledger. Two independent sources, because Shopify only knows about
   * one of them:
   *   discountSavings — original_total_price - total_price, i.e. every
   *     automatic/code discount Shopify allocated. total_discount is the same
   *     number and is used when the older field is missing.
   *   compareAtSavings — compare-at against what the line actually costs before
   *     discounts. compare_at_price is NOT in /cart.js at all, which is why
   *     snippets/ib-cart-drawer-item.liquid stamps data-compare-at-cents and
   *     data-line-original-cents onto each row and this reads the DOM.
   * The gross strikethrough is total + both, so gross - savings == subtotal.
   */
  function buildLedger(input) {
    var cart = input.cart || {};
    var totalCents = intFrom(cart.total_price);

    var compareAtSavings = 0;
    var rowsOriginalTotal = 0;
    var rows = input.rows || [];
    for (var i = 0; i < rows.length; i++) {
      var compareAt = intFrom(rows[i].compareAtCents);
      var lineOriginal = intFrom(rows[i].lineOriginalCents);
      var quantity = intFrom(rows[i].quantity);
      rowsOriginalTotal += lineOriginal;
      if (compareAt > 0 && quantity > 0) {
        compareAtSavings += Math.max(0, compareAt * quantity - lineOriginal);
      }
    }

    // original_total_price is the authority when the cart JSON is in hand. On
    // a cold first paint there is no JSON yet, and the row stamps carry the
    // same number by definition: original_total_price is the sum of every
    // line's original_line_price, which is what data-line-original-cents is.
    var originalTotal = typeof cart.original_total_price === 'number'
      ? cart.original_total_price
      : (typeof cart.total_discount === 'number' ? totalCents + cart.total_discount : rowsOriginalTotal);
    var discountSavings = Math.max(0, originalTotal - totalCents);

    var savings = discountSavings + compareAtSavings;

    return {
      discountSavings: discountSavings,
      compareAtSavings: compareAtSavings,
      savings: savings,
      subtotal: totalCents,
      gross: totalCents + savings
    };
  }

  /*==========================================================================
    Rendering
  ==========================================================================*/

  function progressBar(pct) {
    // scaleX rather than width so the fill animates without layout work; the
    // inner bar is full-width and the transform squeezes it from the left.
    var fill = Math.max(0, Math.min(100, Math.round((pct || 0) * 100)));
    return '<div class="ib-nudge__bar"><i style="transform:scaleX(' + (fill / 100) + ')"></i></div>';
  }

  function moreButton(count) {
    var label = count === 1
      ? themeString('cartMoreOffersOne', '+[count] more offer', { count: count })
      : themeString('cartMoreOffersMany', '+[count] more offers', { count: count });
    return '<button type="button" class="ib-nudge__more" data-ib-nudges-more>' +
      escapeHtml(label) + '</button>';
  }

  function shopLink(url, label) {
    return '<a class="ib-nudge__shop" href="' + escapeHtml(url) + '">' +
      escapeHtml(label || themeString('cartOffersShop', 'Shop')) + ' &rarr;</a>';
  }

  function nudgeRow(messageHtml, pct, rightHtml) {
    return '<div class="ib-nudge">' +
      '<div class="ib-nudge__top"><span class="ib-nudge__msg">' + messageHtml + '</span>' +
      (rightHtml || '') + '</div>' +
      progressBar(pct) +
      '</div>';
  }

  function metRow(message) {
    return '<div class="ib-nudge ib-nudge--met">' + CHECK_SVG +
      '<span>' + escapeHtml(message) + '</span></div>';
  }

  // "Add [n] more ..." with the count in the accent colour, matching the way
  // the free-shipping row emphasises its amount.
  function promoMessage(promo) {
    var template = promo.copy.unmet || promo.copy.label || '';
    var amount = promo.metric === 'value' ? money(promo.remaining) : String(promo.remaining);
    if (template.indexOf('[n]') === -1) {
      return escapeHtml(template);
    }
    var parts = template.split('[n]');
    return escapeHtml(parts[0]) + '<b>' + escapeHtml(amount) + '</b>' + escapeHtml(parts.slice(1).join('[n]'));
  }

  function shippingMessage(shipping) {
    // INVARIANT: this is the ONE renderer that splices a bridged string into
    // HTML without escapeHtml — legal only because cartFreeShippingProgress is
    // bridged from an _html locale key (free_shipping_progress_html), which the
    // t filter does not escape, so decodeEntities is a no-op on it and the
    // merchant-authored markup passes through intact. Never route a non-_html
    // key through this path: decode would revive its entities as live markup.
    // Pinned by tests/cart-drawer/nudge-source.test.js.
    var template = themeString('cartFreeShippingProgress', "You're [amount] away from free shipping");
    var amount = '<b>' + escapeHtml(money(shipping.remaining)) + '</b>';
    if (template.indexOf('[amount]') === -1) {
      return escapeHtml(template);
    }
    return template.split('[amount]').join(amount);
  }

  function renderPinned(model) {
    if (!model.pinned) {
      return '';
    }

    if (model.pinned.kind === 'shipping-unmet') {
      var shipping = model.pinned.shipping;
      var right = model.moreCount > 0
        ? moreButton(model.moreCount)
        : '<span class="ib-nudge__shop ib-nudge__shop--meta">' +
          escapeHtml(themeString('cartFreeShippingMinimum', '[amount] minimum',
            { amount: money(shipping.threshold) })) + '</span>';
      return nudgeRow(shippingMessage(shipping), shipping.pct, right);
    }

    if (model.pinned.kind === 'promo') {
      var promo = model.pinned.promo;
      var promoRight = '';
      if (model.moreCount > 0) {
        promoRight = moreButton(model.moreCount);
      } else if (promo.copy.shopUrl) {
        promoRight = shopLink(promo.copy.shopUrl);
      }
      return nudgeRow(promoMessage(promo), promo.pct, promoRight);
    }

    return metRow(model.pinned.shipping
      ? themeString('cartFreeShippingReached', "You've unlocked free shipping")
      : '');
  }

  function renderMet(model) {
    var html = '';
    for (var i = 0; i < model.metRows.length; i++) {
      html += metRow(model.metRows[i].message);
    }
    return html;
  }

  function renderOffers(candidates) {
    var html = '';
    for (var i = 0; i < candidates.length; i++) {
      var promo = candidates[i];
      var right = promo.copy.shopUrl ? shopLink(promo.copy.shopUrl) : '';
      html += nudgeRow(promoMessage(promo), promo.pct, right);
    }
    return html;
  }

  /*==========================================================================
    Controller
  ==========================================================================*/

  /*
    First-paint cart JSON, stamped by snippets/ib-cart-page.liquid. Promo
    progress needs real line properties, which neither the rendered rows nor the
    .cart__items stamps carry — the drawer buys them with the warm fetch on
    open, and a page has no open. Reading them inline keeps the page's promo
    nudges correct at first paint without adding a request to /cart. A missing
    or malformed stamp is the drawer's existing cold state: shipping only.
  */
  function readSeededCart(root) {
    var node = root.querySelector(SELECTORS.seededCart);
    if (!node) {
      return null;
    }
    try {
      var parsed = JSON.parse(node.textContent || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function IBCartNudges(root, surface) {
    this.root = root;
    this.pinned = root.querySelector(SELECTORS.pinned);
    this.metSlot = root.querySelector(SELECTORS.met);
    this.ledgerSlot = root.querySelector(SELECTORS.ledger);
    this.totals = root.querySelector(SELECTORS.totals);
    this.subtotal = root.querySelector(SELECTORS.subtotal);

    // theme.CartForm stamps its form's data-location on every cart:rendered it
    // dispatches. This is the one that means "our surface".
    this.location = surface === 'page' ? 'page' : 'header';

    this.copy = this.readCopy();
    this.thresholdCents = intFrom(root.getAttribute('data-ib-free-shipping-cents'));

    this.cart = readSeededCart(root);
    this.settling = false;
    this.repaintTimer = null;
    this.warmTimer = null;
    this.dead = false;
    this.warmed = false;
    this.manifestSubscribed = false;
    this.model = null;

    this.bind();
    this.schedule();
  }

  IBCartNudges.prototype = {

    readCopy: function() {
      var node = document.querySelector(SELECTORS.copy);
      if (!node) {
        return {};
      }
      try {
        var parsed = JSON.parse(node.textContent || '{}');
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        // A malformed copy map means no promo nudges, not a broken drawer.
        return {};
      }
    },

    bind: function() {
      var self = this;

      document.addEventListener('cart:updated', function(evt) {
        var detail = evt && evt.detail ? evt.detail : {};

        // Optimistic and count-only updates carry no authoritative totals. The
        // strip freezes on its last settled reading rather than re-pinning a
        // different offer mid-drag; a settled update below thaws it.
        if (detail.optimistic || detail.countOnly) {
          self.markSettling(true);
          return;
        }

        var cartState = null;
        try {
          cartState = theme.cart.getCartState(detail);
        } catch (error) {
          cartState = null;
        }
        if (!cartState) {
          return;
        }

        self.cart = cartState;
        self.markSettling(false);
        self.schedule();
      });

      document.addEventListener('cart:rendered', function(evt) {
        var detail = evt && evt.detail ? evt.detail : {};
        if (detail.location && detail.location !== self.location) {
          return;
        }
        var cartState = detail.cartState || detail.cart || null;
        if (cartState) {
          self.cart = cartState;
          self.markSettling(false);
        }
        // The compare-at half of the ledger is read off the rows that just landed.
        // (NOT MSRP — see the canonical note in snippets/ib-cart-drawer-item.liquid:
        // compare_at_price is the store's compare-at price; true MSRP is a separate
        // unbuilt Blue Link field.)
        self.schedule();
      });

      // Cold open: the shopper clicked the cart icon on a page where nothing
      // has mutated the cart yet, so no event ever carried state. This is the
      // ONLY read, and it can never land on the add path — an add always
      // supplies state through cart:updated first. Inert on the cart page,
      // which never dispatches this and is seeded at first paint instead.
      document.addEventListener('CartDrawer:open', function() {
        self.deferWarm();
      });

      // The engine re-evaluates and announces whenever it has a manifest and a
      // cart, which is the signal that a promotional row can exist at all. On a
      // fast page that lands after our first paint, and without this the strip
      // would stay shipping-only until the shopper next touched the cart.
      document.addEventListener('ibgift:state', function() {
        self.schedule();
      });

      this.subscribeToManifest();

      if (this.pinned && this.pinned.addEventListener) {
        this.pinned.addEventListener('click', function(evt) {
          var target = evt.target;
          if (target && target.closest && target.closest(SELECTORS.moreBtn)) {
            evt.preventDefault();
            self.openOffers(target.closest(SELECTORS.moreBtn));
          }
        });
      }
    },

    /*
      The manifest loader is a LATER deferred script than this one
      (assets/ib-gift-manifest-loader.js is pulled in by the gift-engine
      snippet, which theme.liquid renders after the pinned script chain), so at
      boot window.IBGiftManifest is GUARANTEED absent and subscribing only there
      registers nothing at all. Try now anyway — a future load order, or a test
      harness, may already have it — and try again once the deferred chain has
      run. get() hands back the loader's existing promise, so a second attempt
      costs no request, and the flag makes sure the repaint is wired once.
    */
    subscribeToManifest: function() {
      var self = this;

      function attach() {
        if (self.dead || self.manifestSubscribed) {
          return;
        }
        try {
          var loader = window.IBGiftManifest;
          if (!loader || typeof loader.get !== 'function') {
            return;
          }
          self.manifestSubscribed = true;
          loader.get().then(function() {
            self.schedule();
          }, function() { /* fail open: shipping-only */ });
        } catch (error) {
          /* fail open: shipping-only */
        }
      }

      attach();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', attach);
      } else if (typeof setTimeout === 'function') {
        setTimeout(attach, 0);
      }
    },

    /*
      The drawer controller emits CartDrawer:open from inside its OWN
      cart:updated listener, which script order puts ahead of ours — so at the
      moment this fires on an add, self.cart is still null even though the very
      same event is carrying the cart to us. Deciding to read here would buy
      with a request what is already a tick away. Defer to the end of the event
      turn: by then every cart:updated listener has run and warm() sees the
      truth. That is the module's own rule (never fetch on the add path), kept.
    */
    deferWarm: function() {
      if (this.dead || this.warmTimer || this.warmed) {
        return;
      }
      var self = this;
      this.warmTimer = setTimeout(function() {
        self.warmTimer = null;
        self.warm();
      }, 0);
    },

    warm: function() {
      if (this.dead || this.cart || this.warmed) {
        return;
      }
      if (typeof theme === 'undefined' || !theme.cart || typeof theme.cart.getCart !== 'function') {
        return;
      }
      if (theme.cart.isChallengeActive && theme.cart.isChallengeActive()) {
        return;
      }

      this.warmed = true;
      var self = this;
      Promise.resolve(theme.cart.getCart())
        .then(function(cart) {
          /*
            The guard above ran BEFORE this request went out, and the shopper
            can mutate the cart while it is in flight — cart:updated (or
            cart:rendered) then hands us authoritative state NEWER than this
            reply carries. warm() exists only to fill a cart we never had, so
            once self.cart is set this reading is by definition the older one,
            and applying it would rewind the shipping/promo copy until the next
            mutation happens to correct it. Re-check the very field the entry
            guard reads. Same for a teardown that landed mid-flight.
          */
          if (self.dead || self.cart) {
            return;
          }
          var cartState = theme.cart.getCartState ? theme.cart.getCartState(cart) : cart;
          if (!cartState) {
            return;
          }
          self.cart = cartState;
          self.schedule();
        })
        .catch(function() {
          // Fail open: the next cart event supplies state.
        });
    },

    markSettling: function(settling) {
      this.settling = Boolean(settling);
      if (this.pinned && this.pinned.classList) {
        this.pinned.classList.toggle('is-settling', this.settling);
      }
    },

    schedule: function() {
      if (this.dead) {
        return;
      }
      var self = this;
      if (this.repaintTimer) {
        clearTimeout(this.repaintTimer);
      }
      this.repaintTimer = setTimeout(function() {
        self.repaintTimer = null;
        self.paint();
      }, REPAINT_MS);
    },

    /*======================================================================
      Inputs
    ======================================================================*/

    // Fail-open chain, in order: no engine -> shipping only; no manifest ->
    // shipping only; a throwing engine -> shipping only. The shipping nudge
    // never depends on the gift engine at all.
    progressRows: function() {
      try {
        var engine = window.IBGiftEngine;
        if (!engine || typeof engine.progress !== 'function') {
          return [];
        }
        if (typeof engine.getManifest === 'function' && !engine.getManifest()) {
          return [];
        }
        var lines = this.cart && Array.isArray(this.cart.items) ? this.cart.items : [];
        return engine.progress(lines, Date.now()) || [];
      } catch (error) {
        return [];
      }
    },

    /*
     * Totals to paint from. theme.cart state when we have it; otherwise the
     * .cart__items stamps the section already rendered (data-count /
     * data-cart-subtotal are the same pair theme.CartForm falls back to). The
     * snapshot deliberately carries NO items: promo progress needs real line
     * properties, so a cold drawer shows shipping only until warm() lands.
     */
    cartSnapshot: function() {
      if (this.cart) {
        return this.cart;
      }
      var items = this.root.querySelector(SELECTORS.items);
      if (!items || !items.dataset) {
        return null;
      }
      return {
        item_count: intFrom(items.dataset.count),
        total_price: intFrom(items.dataset.cartSubtotal),
        items: []
      };
    },

    // Row facts the ledger needs that /cart.js does not carry.
    ledgerRows: function() {
      var out = [];
      var rows = this.root.querySelectorAll(SELECTORS.row);
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var qtyInput = row.querySelector(SELECTORS.qtyInput);
        out.push({
          compareAtCents: row.dataset ? row.dataset.compareAtCents : 0,
          lineOriginalCents: row.dataset ? row.dataset.lineOriginalCents : 0,
          quantity: qtyInput ? qtyInput.value : 0
        });
      }
      return out;
    },

    /*======================================================================
      Paint
    ======================================================================*/

    paint: function() {
      if (this.dead) {
        return;
      }
      try {
        this.paintNudges();
        this.paintLedger();
      } catch (error) {
        this.selfDestruct(error);
      }
    },

    paintNudges: function() {
      // Frozen mid-burst: keep the last settled strip on screen untouched.
      if (this.settling && this.model) {
        return;
      }

      var cart = this.cartSnapshot() || {};
      var model = buildModel({
        thresholdCents: this.thresholdCents,
        totalCents: cart.total_price,
        itemCount: intFrom(cart.item_count),
        rows: this.progressRows(),
        copy: this.copy
      });

      this.model = model;

      if (this.pinned) {
        var pinnedHtml = renderPinned(model);
        this.pinned.innerHTML = pinnedHtml;
        setHidden(this.pinned, !pinnedHtml);
      }

      if (this.metSlot) {
        var metHtml = renderMet(model);
        this.metSlot.innerHTML = metHtml;
        setHidden(this.metSlot, !metHtml);
      }

      this.paintOffers(model);
    },

    paintOffers: function(model) {
      var modal = document.querySelector(SELECTORS.offersModal);
      if (!modal) {
        return;
      }
      var list = modal.querySelector(SELECTORS.offersList);
      if (list) {
        list.innerHTML = renderOffers(model.candidates);
      }
      var empty = modal.querySelector(SELECTORS.offersEmpty);
      if (empty) {
        setHidden(empty, model.candidates.length > 0);
      }
      // An offer list emptied by the last mutation must not strand an open modal.
      if (!model.candidates.length && !modal.hidden) {
        this.closeOffers(modal);
      }
    },

    paintLedger: function() {
      if (!this.ledgerSlot) {
        return;
      }

      var cart = this.cartSnapshot() || {};
      var ledger = buildLedger({
        cart: cart,
        rows: this.ledgerRows()
      });

      var show = intFrom(cart.item_count) > 0 && ledger.savings > 0;

      this.ledgerSlot.innerHTML = show
        ? '<div class="ib-cart-ledger__row ib-cart-ledger__row--save">' +
          '<span class="ib-cart-ledger__key">' +
          escapeHtml(themeString('cartSavingsLabel', 'Savings')) + '</span>' +
          '<span class="ib-cart-ledger__value">&minus;' + escapeHtml(money(ledger.savings)) + '</span>' +
          '</div>'
        : '';
      setHidden(this.ledgerSlot, !show);

      this.paintGross(show ? ledger.gross : 0);
    },

    // The gross strikethrough is a SIBLING of [data-subtotal], never a child:
    // theme.CartForm replaces that element's innerHTML wholesale on every
    // render, so anything inside it is destroyed on the next mutation.
    paintGross: function(grossCents) {
      if (!this.totals || !this.subtotal) {
        return;
      }

      if (!this.grossEl) {
        if (typeof document.createElement !== 'function') {
          return;
        }
        this.grossEl = document.createElement('span');
        this.grossEl.className = GROSS_CLASS;
        this.totals.insertBefore(this.grossEl, this.subtotal);
      }

      if (grossCents > 0) {
        this.grossEl.textContent = money(grossCents);
        setHidden(this.grossEl, false);
      } else {
        this.grossEl.textContent = '';
        setHidden(this.grossEl, true);
      }
    },

    /*======================================================================
      Offers modal — opened through the controller so Escape, the scrim and
      focus restoration keep working through ONE ordered document handler.
    ======================================================================*/

    openOffers: function(trigger) {
      var modal = document.querySelector(SELECTORS.offersModal);
      if (!modal) {
        return;
      }
      var drawer = window.IBCartDrawer;
      if (drawer && typeof drawer.openModal === 'function') {
        drawer.openModal(modal, trigger);
        return;
      }
      setHidden(modal, false);
    },

    closeOffers: function(modal) {
      var drawer = window.IBCartDrawer;
      if (drawer && typeof drawer.closeModal === 'function') {
        drawer.closeModal(modal, true);
        return;
      }
      setHidden(modal, true);
    },

    /*======================================================================
      Failure
    ======================================================================*/

    // The drawer is the product; the strip is a garnish. On an unexpected
    // throw the slots leave the document entirely rather than sitting there
    // half-painted, and nothing repaints again this page load.
    selfDestruct: function(error) {
      this.dead = true;
      if (this.repaintTimer) {
        clearTimeout(this.repaintTimer);
        this.repaintTimer = null;
      }
      if (this.warmTimer) {
        clearTimeout(this.warmTimer);
        this.warmTimer = null;
      }

      [this.pinned, this.metSlot, this.ledgerSlot, this.grossEl].forEach(function(node) {
        if (node && node.parentNode && typeof node.parentNode.removeChild === 'function') {
          node.parentNode.removeChild(node);
        }
      });
      this.pinned = null;
      this.metSlot = null;
      this.ledgerSlot = null;
      this.grossEl = null;

      if (window.console && window.console.warn) {
        window.console.warn('[IBCartNudges] disabled after an error', error);
      }
    }
  };

  /*
    Two queries rather than one comma-joined selector, matching
    assets/ib-cart-drawer.js: the drawer and the cart page are mutually
    exclusive by URL, so this is a stated precedence rather than a race.
  */
  function boot() {
    var root = document.querySelector(SELECTORS.root);
    var surface = 'drawer';

    if (!root) {
      root = document.querySelector(SELECTORS.pageRoot);
      surface = 'page';
    }

    if (!root || !root.querySelector(SELECTORS.pinned)) {
      return;
    }
    if (typeof theme === 'undefined' || !theme || !theme.cart) {
      return;
    }

    try {
      window.IBCartNudges = new IBCartNudges(root, surface);
    } catch (error) {
      if (window.console && window.console.warn) {
        window.console.warn('[IBCartNudges] boot failed', error);
      }
    }
  }

  // Exposed for tests: the two pure functions carry every decision worth
  // pinning, and they are the only thing here with no DOM in it.
  if (typeof window !== 'undefined') {
    window.IBCartNudgesModel = {
      buildModel: buildModel,
      buildLedger: buildLedger,
      renderPinned: renderPinned,
      renderMet: renderMet,
      renderOffers: renderOffers
    };
  }

  boot();
})();
